import React, { ChangeEvent, useContext, useState } from 'react';

import FilterPlatesContext from 'contexts/FilterPlatesContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import aguid from 'lib/aguid';
import STORE from 'store';
import NewQuickFilterForm from '../../CustomerLaneCommitsBoard/Settings/QuickFilters/NewQuickFilterForm/NewQuickFilterForm';
import { FilterBarSettings } from '../filterBarSettings';

const NewReportQuickFilterFormContainer = ({
  settings,
  close,
  quickFilter,
}: {
  settings: FilterBarSettings;
  close: () => void;
  quickFilter?: LegacySavedFilter;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { dataTypes } = useContext(FilterPlatesContext);
  const isEditing = quickFilter !== undefined;
  const [name, setName] = useState<string>(quickFilter?.name ?? '');
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>(
    quickFilter?.drillDowns ?? [],
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const isValid = drillDowns.length !== 0;

  const onSave = () => {
    if (!isValid) {
      return;
    }

    const filter: LegacySavedFilter = {
      version: '2',
      id: quickFilter ? quickFilter.id : aguid(), // editing overwrites the same doc
      scope: [], // report and dashboard quick filters are not scoped to specific entities
      drillDowns,
      source: settings.quickFilterSource, // namespaced so useQuickFilters can query only this report's or dashboard's filters
      name,
      dateField: 'date',
    };

    setIsSaving(true);
    STORE.filterDefinitions
      .getLegacySavedFiltersRef({ accountId: selectedAccountId })
      .doc(filter.id)
      .set(filter)
      .then(async () => {
        // Editing keeps the same id, so the order is unchanged — only append
        // for a brand-new filter not already in the order.
        if (!settings.quickFilterOrder.includes(filter.id)) {
          await settings.save({
            quickFilterOrder: [...settings.quickFilterOrder, filter.id],
          });
        }
        setIsSaving(false);
        close();
      });
  };

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      dataTypes={dataTypes}
    >
      <NewQuickFilterForm
        name={name}
        onNameChanged={onNameChanged}
        close={close}
        onSave={onSave}
        isSaving={isSaving}
        isValid={isValid}
        title={isEditing ? 'Edit Quick Filter' : 'New Quick Filter'}
        saveLabel={isEditing ? 'Save Changes' : 'Save'}
      />
    </FilterPlatesProvider>
  );
};

export default NewReportQuickFilterFormContainer;
